/**
 * Decorative side ornament.
 *
 * Implementation: a single SVG `<pattern>` tile of a stylised oak leaf
 * that the browser repeats vertically by itself. Because we set the
 * outer SVG to 100% width / 100% height and use `<rect width=100%
 * height=100%>` filled with that pattern, the band automatically
 * covers whatever the viewport's height happens to be - no fixed
 * leaf count, no clipping on tall screens.
 *
 * Sits in a `position: fixed` element pinned to the viewport edge so
 * the ornament stays visible while the visitor scrolls.
 */
export function SideOrnament({ side }: { side: "left" | "right" }) {
  const transform = side === "right" ? "scaleX(-1)" : undefined;
  const patternId = "orn-pattern-" + side;
  return (
    <div
      aria-hidden
      className="hidden xl:block fixed top-0 bottom-0 w-14 pointer-events-none select-none z-0 overflow-hidden"
      style={{
        [side]: 0,
        transform,
        opacity: 0.55,
      } as React.CSSProperties}
    >
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
           style={{ display: "block" }}>
        <defs>
          {/*
            The pattern tile is 60 x 90 user units. The browser repeats
            it as many times as needed to fill the surrounding rect,
            no matter how tall the viewport gets.
          */}
          <pattern id={patternId} x="-2" y="0" width="60" height="90" patternUnits="userSpaceOnUse">
            <path d="M30 6 C18 22, 12 38, 18 52 C22 60, 28 64, 30 72 C32 64, 38 60, 42 52 C48 38, 42 22, 30 6 Z"
                  fill="var(--color-accent)" opacity="0.85"/>
            <path d="M30 14 L30 70" stroke="var(--color-primary)" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
            <path d="M30 26 Q22 30 18 36 M30 36 Q22 40 18 46 M30 46 Q22 50 18 56
                     M30 26 Q38 30 42 36 M30 36 Q38 40 42 46 M30 46 Q38 50 42 56"
                  stroke="var(--color-primary)" strokeWidth="0.8" fill="none" opacity="0.5"/>
          </pattern>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </div>
  );
}
